import React, { ReactNode, useCallback, useMemo, useRef } from 'react';
import { useDispatch } from 'react-redux';

import { StringKeyOf } from 'ia-react-core/~/interfaces/StringKeyOf';
import { AxiosRequestConfig } from 'axios';
import findChildByType from 'ia-react-core/~/utils/components/_findChildByType';
import requestResourceById from '../../../actions/resource-fn/requestResourceById';
import Mapper from '../../redux/Mapper';
import DataLoader, { DataLoaderProps } from '../DataLoader';
import ResourceMapper, { ResourceMapperProps } from '../../resource/_ResourceMapper';
import filterPropsByComponent from '../../../utils/components/_filterPropsByComponent';
import { Resources, Content } from './ResourceLoader.compounds';
import ResourceGroup from '../../resource/ResourceGroup';

export interface ResourceLoadRenderProps<Data> {
  resourceId?: number;
  data: Data;
  responseData?: unknown;
}

export interface ResourceLoaderProps<Data, Error = {}> extends
  Omit<ResourceMapperProps<Data, Error>, 'children' | 'data'>,
  Omit<DataLoaderProps<Data, Error>, 'data' | 'children' | 'onRetry'> {
  /** React children nodes, or function that will receive an object with a `data` property containing the loaded data. */
  children: ReactNode | ((values: ResourceLoadRenderProps<Data>) => ReactNode);
  /** Location of an existing Resource. Use either `resourceSrc` or `url`. */
  resourceSrc?: string;
  /** Text of the retry button. */
  onlyUpdateOnRequest?: boolean;
  /** Given the response data, return the data to pass to the loader. */
  getDataFromResponse?: (data: any, headers?: any) => Data;
  inlineErrorMessage?: boolean;
}

export interface ResourceLoaderMock {
  mockRenderProps: (props: ResourceLoadRenderProps<any>) => void;
  mockData: (data: any) => void;
  mockResponseData: (data: any) => void;
  renderProps: ResourceLoadRenderProps<any>;
  data: any;
  responseData: any;
}

const ResourceLoaderDefaultProps = {
  onlyUpdateOnRequest: false,
  inlineErrorMessage: true,
};

/**
 * @mode developer
 */
const ResourceLoader = <Data, Error = {}>({
  children, name, CustomLoader, onlyUpdateOnRequest, buttonAlign, getDataFromResponse = (data: unknown) => data as Data, onRequest, ...props
}: ResourceLoaderProps<Data, Error>) => {
  const dispatch = useDispatch();

  const resources = findChildByType(children, Resources);
  const content = findChildByType(children, Content);

  const lastConfigRef = useRef<AxiosRequestConfig>({});

  const dispatchRequestResourceById = useCallback((...args: Parameters<typeof requestResourceById>) => dispatch(requestResourceById(...args)), [dispatch]);

  const handleRequest = useCallback((config: AxiosRequestConfig) => {
    const newConfig = onRequest ? onRequest(config) : config;
    lastConfigRef.current = newConfig as AxiosRequestConfig;
    return newConfig;
  }, [onRequest]);

  const group = useMemo(() => {
    if (!resources) return null;
    return (
      <ResourceGroup name={name} autoRequest {...resources.props} />
    );
  }, [name, resources]);

  const resourceProps = useMemo(() => {
    if (!resources) return { name };
    return { resourceSrc: name };
  }, [name, resources]);

  const resolvedContent = useMemo(() => {
    if (content) return content.props.children;
    return children;
  }, [children, content]);

  return (
    <>
      {group}
      <ResourceMapper<Data, Error>
        autoRequest
        {...filterPropsByComponent(props, ResourceMapper<Data, Error>)}
        onRequest={handleRequest}
        {...resourceProps}
      >
        {({ resourceId, pending, error, attempts, dataDst: resourceDataDst, maxAttempts, requestCount, isGroup }) => (
          <Mapper<{ mappedData: unknown }>
            src={{ mappedData: resourceDataDst }}
            cacheKey={onlyUpdateOnRequest ? requestCount : undefined}
          >
            {({ mappedData }) => (
              <DataLoader<Data, Error>
                {...filterPropsByComponent(props, DataLoader<Data, Error>)}
                pending={pending}
                CustomLoader={CustomLoader}
                error={error}
                data={mappedData && getDataFromResponse(mappedData)}
                isDataList={Array.isArray(resourceDataDst)}
                attempts={attempts}
                maxAttempts={maxAttempts}
                onRetry={() => dispatchRequestResourceById(resourceId, lastConfigRef.current.data, lastConfigRef.current)}
                buttonAlign={buttonAlign}
                isGroup={isGroup}
              >
                {() => typeof resolvedContent === 'function' ? resolvedContent({
                  resourceId,
                  responseData: mappedData,
                  data: mappedData && getDataFromResponse(mappedData),
                }) : resolvedContent}
              </DataLoader>
            )}
          </Mapper>
        )}
      </ResourceMapper>
    </>
  );
};

ResourceLoader.defaultProps = ResourceLoaderDefaultProps;
ResourceLoader.publicProps = [...ResourceMapper.publicProps, ...DataLoader.publicProps, 'getDataFromResponse'] as StringKeyOf<ResourceLoaderProps<{}>>[];
ResourceLoader.Resources = Resources;
ResourceLoader.Content = Content;

export default ResourceLoader;
